/**
 * Multi-account storage: profiles and switch tokens in localStorage so every tab
 * sees the same saved sessions (sessionStorage is tab-scoped and broke multi-tab).
 *
 * HttpOnly cookie is the primary session for the active account; stored Bearer
 * tokens are used for account switching and add-account flows.
 */

export const ACCOUNTS_KEY = "chairside_accounts";
export const ACTIVE_KEY = "activeAccountId";
export const TOKENS_KEY = "chairside_account_tokens";

const migrateTokensFromSessionStorage = () => {
  try {
    const legacy = sessionStorage.getItem(TOKENS_KEY);
    if (!legacy) return;

    const legacyMap = JSON.parse(legacy) || {};
    const localRaw = localStorage.getItem(TOKENS_KEY);
    const localMap = localRaw ? JSON.parse(localRaw) : {};
    localStorage.setItem(
      TOKENS_KEY,
      JSON.stringify({ ...localMap, ...legacyMap }),
    );
    sessionStorage.removeItem(TOKENS_KEY);
  } catch {
    /* ignore */
  }
};

migrateTokensFromSessionStorage();

const readTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY)) || {};
  } catch {
    return {};
  }
};

const writeTokens = (map) => {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(map));
};

const readAccountsRaw = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(ACCOUNTS_KEY)) || [];
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

/** Migrate legacy rows that embedded `token` into token storage. */
const normalizePersistedAccounts = (rows) => {
  const tokens = readTokens();
  let changed = false;
  const profiles = rows.map((row) => {
    const { token, ...rest } = row;
    if (token && rest.userId) {
      if (!tokens[rest.userId]) {
        tokens[rest.userId] = token;
        changed = true;
      }
    }
    return rest;
  });
  if (changed) writeTokens(tokens);
  return profiles;
};

const readProfiles = () => normalizePersistedAccounts(readAccountsRaw());

const writeProfiles = (profiles) => {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(profiles));
};

const toProfile = (user) => ({
  userId: user._id,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  role: user.role,
  avatar: user.avatar || null,
  organizationId:
    user.organizationId?._id ||
    user.organizationId ||
    user.selectedOrganizationId?._id ||
    user.selectedOrganizationId ||
    null,
  organizationName:
    user.organizationId?.name || user.selectedOrganizationId?.name || null,
});

export const getAccounts = () => {
  const profiles = readProfiles();
  const tokens = readTokens();
  return profiles.map((p) => ({
    ...p,
    token: tokens[p.userId] || null,
  }));
};

export const getActiveAccountId = () => localStorage.getItem(ACTIVE_KEY);

export const getAccountToken = (userId) => {
  if (!userId) return null;
  return readTokens()[userId] || null;
};

export const getActiveAccountToken = () => {
  const id = getActiveAccountId();
  return id ? getAccountToken(id) : null;
};

export const setAccountToken = (userId, token) => {
  const map = readTokens();
  if (token) map[userId] = token;
  else delete map[userId];
  writeTokens(map);
};

/** Save profile; token: string = store Bearer, null = remove stored Bearer, undefined = leave unchanged */
export const upsertAccount = (user, token) => {
  const profiles = readProfiles();
  const profile = toProfile(user);
  const idx = profiles.findIndex((a) => a.userId === profile.userId);
  if (idx >= 0) profiles[idx] = profile;
  else profiles.push(profile);
  writeProfiles(profiles);

  if (typeof token === "string" && token.length > 0) {
    setAccountToken(profile.userId, token);
  } else if (token === null) {
    setAccountToken(profile.userId, null);
  }

  localStorage.setItem(ACTIVE_KEY, profile.userId);
  return getAccounts();
};

export const removeAccount = (userId) => {
  const profiles = readProfiles().filter((a) => a.userId !== userId);
  writeProfiles(profiles);
  setAccountToken(userId, null);
  if (localStorage.getItem(ACTIVE_KEY) === userId) {
    localStorage.removeItem(ACTIVE_KEY);
  }
  return getAccounts();
};

/** Remove every saved session tied to a deleted organization. */
export const removeAccountsForDeletedOrganization = ({
  organizationId,
  organizationName,
} = {}) => {
  const orgIdStr = organizationId ? String(organizationId) : null;
  const nameNorm = organizationName?.trim().toLowerCase() || null;
  if (!orgIdStr && !nameNorm) return getAccounts();

  const profiles = readProfiles();
  const userIdsToRemove = profiles
    .filter((profile) => {
      if (
        orgIdStr &&
        profile.organizationId &&
        String(profile.organizationId) === orgIdStr
      ) {
        return true;
      }
      if (
        nameNorm &&
        profile.organizationName?.trim().toLowerCase() === nameNorm
      ) {
        return true;
      }
      return false;
    })
    .map((profile) => profile.userId);

  userIdsToRemove.forEach((userId) => removeAccount(userId));
  return getAccounts();
};

export const setActiveAccount = (userId) => {
  const profiles = readProfiles();
  const account = profiles.find((a) => a.userId === userId);
  if (!account) return null;
  localStorage.setItem(ACTIVE_KEY, userId);
  const tokens = readTokens();
  return { ...account, token: tokens[userId] || null };
};

export const clearAllAccounts = () => {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(ACTIVE_KEY);
  localStorage.removeItem(TOKENS_KEY);
  sessionStorage.removeItem(TOKENS_KEY);
};
